import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";
import { datadogLogs } from "@datadog/browser-logs";
import { useParams } from "@remix-run/react";
import * as Sentry from "@sentry/react";
import mixpanel from "mixpanel-browser";

import { supabase } from "~/integrations/supabase/ssr.browser";

import { ENABLE_ANALYTICS } from "./constants";
import { getBrowserEnvVar } from "./env.shared";
import type { MixpanelEvent } from "./events";
import { logger } from "./logger";
import { setSentryUser, setupSentry } from "../lib/sentry/client";

// Hook to get sessionId from URL params
export const useSessionIdFromUrl = () => {
    const params = useParams();
    return params.sessionId;
};

/* Simple heuristic to determine if the user is a recipient or owner based on the URL 
	This breaks when the client is opening their own sharing link, but this is hopefully 
	rare an edge case.
*/

export const getOwnershipFromPath = (pathname: string): string | undefined => {
    const isSharingLink = pathname.includes("/sharing");
    return isSharingLink ? "recipient" : "owner";
};

// Function to extract sessionId from pathname
export const getSessionIdFromPath = (pathname: string): string | undefined => {
    const sessionIdMatch = pathname.match(/\/sessions\/([^/]+)/);
    return sessionIdMatch ? sessionIdMatch[1] : undefined;
};

// ----------------------------------------------
// Customer.io (browser) – initialize once
// ----------------------------------------------

let customerIo: AnalyticsBrowser | null = null;
let lastCustomerIoPayload: Record<string, unknown> | null = null;

// AIDEV-NOTE: Track if analytics has been initialized to prevent duplicate initialization
let analyticsInitialized = false;

// AIDEV-NOTE: Track if Mixpanel is ready to receive events
let mixpanelReady = false;

// AIDEV-NOTE: Queue for events sent before Mixpanel initialization
interface QueuedEvent {
    name: string;
    props: Record<string, unknown>;
}
const eventQueue: QueuedEvent[] = [];

// AIDEV-NOTE: Current userType cached for enriching events and late identifies

// AIDEV-NOTE: Unused equality helper removed to reduce complexity

/**
 * Identify (create/update) a user in Customer.io.
 *
 * We intentionally run this in the browser because the server does not have access
 * to the end-user's auth state. The request is authenticated via Basic auth where
 * the username is the API key and the password is left blank. We therefore send
 * the header `Authorization: Basic ${btoa(`${apiKey}:`)}` as per Customer.io docs.
 */
const identifyCustomerIoUser = async (
    user: {
        id: string;
        email?: string;
        user_metadata?: { name?: string };
    } | null,
    extraTraits?: Record<string, unknown>,
) => {
    if (!user || !customerIo) return;

    const payload = {
        userId: user.id,
        email: user.email,
        name: user.user_metadata?.name,
        ...(extraTraits || {}),
    };

    // Only call identify if the payload is different from the last one
    if (JSON.stringify(payload) === JSON.stringify(lastCustomerIoPayload)) {
        return;
    }

    try {
        lastCustomerIoPayload = { ...payload }; // Store a copy of the successful payload
        await customerIo.identify(user.id, payload);
    } catch (error) {
        captureException("Failed to identify user in Customer.io", error);
    }
};

// ---------------------------
// Identity helpers (modularized)
// ---------------------------

export type UserType = "client" | "practitioner" | "consultant" | "unknown";
let currentUserType: UserType | undefined = undefined;

type AnalyticsUser =
    | {
          id: string;
          email?: string;
          user_metadata?: { name?: string };
          is_anonymous?: boolean;
      }
    | null
    | undefined;

// AIDEV-NOTE: Applies logger + Sentry identity, respecting consent for PII in logs
const applyLoggerAndSentry = (user: AnalyticsUser, hasConsent: boolean) => {
    // Set user context in Sentry (ok without consent; kept minimal)
    setSentryUser(user?.id || null, user?.email || null);

    // Add context to logger only if consent; otherwise anonymous flag only
    if (hasConsent && user) {
        logger.withContext({ userId: user.id, userEmail: user.email });
    } else {
        logger.withContext({ isAuthenticated: !!user });
    }
};

// AIDEV-NOTE: Updates Customer.io profile; include userType only with consent
const applyCustomerIo = (
    user: AnalyticsUser,
    userType: UserType | undefined,
    hasConsent: boolean,
) => {
    if (!user || user.is_anonymous) return;
    const traits = hasConsent && userType ? { userType } : undefined;
    if (traits) currentUserType = userType;
    identifyCustomerIoUser(user, traits);
};

// AIDEV-NOTE: Updates Mixpanel identity + profile + super props; only with consent
const applyMixpanel = (
    user: AnalyticsUser,
    userType: UserType | undefined,
    hasConsent: boolean,
) => {
    if (!hasConsent || !user) return;

    // AIDEV-NOTE: Ensure proper identity merge: alias anon -> user, then identify
    try {
        const currentId = (mixpanel as any)?.get_distinct_id?.();
        if (
            currentId &&
            currentId !== user.id &&
            safeGet(`mp_alias_${user.id}`) !== "true"
        ) {
            // Merge the pre-login anonymous profile into the user profile
            mixpanel.alias(user.id);
            safeSet(`mp_alias_${user.id}`, "true");
        }
    } catch {
        /* ignore alias errors */
    }

    mixpanel.identify(user.id);
    // AIDEV-NOTE: Use camelCase userType consistently for Mixpanel People profile
    mixpanel.people.set({
        $email: user.email,
        $name: user.user_metadata?.name,
        ...(userType ? { userType } : {}),
    });
    if (userType) {
        currentUserType = userType;
        // Super properties (events): register userType for all subsequent events
        mixpanel.register({ userType });
        // Ensure initial write in case profile missing property
        mixpanel.people.set_once({ ...(userType ? { userType } : {}) });
    }
};

// AIDEV-NOTE: Single entry to update all identities consistently
const applyIdentities = (
    user: AnalyticsUser,
    userType: UserType | undefined,
    hasConsent: boolean,
) => {
    applyLoggerAndSentry(user, hasConsent);
    applyCustomerIo(user, userType, hasConsent);
    applyMixpanel(user, userType, hasConsent);
};

// ---------------------------
// Event queue helper
// ---------------------------

// AIDEV-NOTE: Flush queued events after Mixpanel initialization
const flushEventQueue = () => {
    if (eventQueue.length > 0) {
        // AIDEV-NOTE: Converted console.log to structured logger
        logger
            .withMetadata({ queuedEvents: eventQueue.length })
            .info("[Analytics] Flushing queued events");
        const eventsToFlush = [...eventQueue];
        eventQueue.length = 0; // Clear the queue

        eventsToFlush.forEach(({ name, props }) => {
            try {
                trackEvent(name as MixpanelEvent["name"], props);
            } catch (error) {
                captureException(
                    `Failed to flush queued event: ${name}`,
                    error,
                );
            }
        });
    }
};

// ---------------------------
// Customer.io SDK helper
// ---------------------------

const sendCustomerIoEvent = async (
    event: string,
    properties: Record<string, unknown>,
    explicitUserId?: string | null,
) => {
    if (!customerIo) return;

    try {
        const userId = explicitUserId ?? undefined;

        await customerIo.track(event, { userId, ...properties });
    } catch (error) {
        captureException("Failed to send Customer.io event", error);
    }
};

export const initAnalytics = (
    user: AnalyticsUser,
    options: { userType?: UserType },
) => {
    // AIDEV-NOTE: Prevent duplicate initialization in StrictMode or multiple renders
    if (analyticsInitialized) {
        // Still update user context if a user is provided
        applyIdentities(
            user,
            options?.userType ?? currentUserType,
            getHasAnalyticsConsent(),
        );
        return;
    }

    if (!ENABLE_ANALYTICS) {
        logger.info("Analytics disabled in development mode");
        return;
    }

    // Mark as initialized early to prevent race conditions
    analyticsInitialized = true;

    const customerIoWriteKey = getBrowserEnvVar("CUSTOMERIO_API_KEY");
    customerIo = customerIoWriteKey
        ? AnalyticsBrowser.load({ writeKey: customerIoWriteKey })
        : null;

    if (!customerIo) {
        captureException(
            "Customer.io not initialized",
            new Error("Customer.io not initialized"),
        );
    }

    const hasConsent = getHasAnalyticsConsent();

    try {
        // Initialize Sentry (error tracking is legitimate interest - no PII)
        setupSentry();

        const datadogClientToken = getBrowserEnvVar("DATADOG_CLIENT_TOKEN");
        // Initialize Datadog - only for essential error logging without consent
        if (datadogClientToken) {
            datadogLogs.init({
                clientToken: datadogClientToken,
                forwardErrorsToLogs: true,
                site: "datadoghq.com",
                service: "frontend",
                env: getBrowserEnvVar("ENV_NAME") || "unknown",
            });
        }

        // Initialize Mixpanel - GDPR compliant mode
        const mixpanelProjectToken = getBrowserEnvVar(
            "VITE_APP_MIXPANEL_PROJECT_TOKEN",
        );
        if (mixpanelProjectToken) {
            mixpanel.init(mixpanelProjectToken, {
                track_pageview: false,
                persistence: isLocalStorageWritable ? "localStorage" : "cookie",
                disable_persistence: !isLocalStorageWritable,
                ignore_dnt: true,
                // Start with tracking disabled until consent
                opt_out_tracking_by_default: !hasConsent,
            });

            // If no consent, only track anonymous events
            if (!hasConsent) {
                // Disable user profile tracking without consent
                mixpanel.set_config({ disable_persistence: true });
            } else {
                const hasOptedOut = mixpanel.has_opted_out_tracking();
                if (hasOptedOut) {
                    mixpanel.opt_in_tracking();
                }
                flushEventQueue();
            }

            // AIDEV-NOTE: Mark Mixpanel as ready and flush queued events
            mixpanelReady = true;
        }

        // AIDEV-NOTE: Handle initial user if provided from AnalyticsProvider
        applyIdentities(
            user,
            options?.userType ?? currentUserType,
            getHasAnalyticsConsent(),
        );

        supabase.auth.onAuthStateChange((event, session) => {
            if (event !== "INITIAL_SESSION" && event !== "SIGNED_IN") {
                logger
                    .withMetadata({ event, hasUser: !!session?.user })
                    .debug("[Auth] state change:");
            }
            applyIdentities(
                session?.user,
                currentUserType,
                getHasAnalyticsConsent(),
            );
        });

        logger.info("Analytics initialized successfully");
    } catch (error) {
        logger
            .withError(error as Error)
            .error("Failed to initialize analytics");
    }
};

const getEnrichedProps = () => {
    const sessionIdFromUrl = getSessionIdFromPath(window.location.pathname);
    const ownership = getOwnershipFromPath(window.location.pathname);

    // AIDEV-NOTE: Environment detection for analytics - uses VITE_APP_ENV_NAME
    const environment = getBrowserEnvVar("ENV_NAME") || "unknown";

    return {
        ...(sessionIdFromUrl && { session: sessionIdFromUrl }),
        ...(ownership && { ownership }),
        ...(currentUserType ? { userType: currentUserType } : {}),
        environment,
    };
};

// 2. Define the trackEvent function so that TypeScript will infer the
//    expected props from the event name.
// 2. Define the trackEvent function so that TypeScript will infer the
//    expected props from the event name.
export function trackEvent<E extends MixpanelEvent["name"]>(
    name: E,
    props: Extract<MixpanelEvent, { name: E }>["props"],
): void {
    try {
        const extraProps = getEnrichedProps();

        const enrichedProps = {
            ...props,
            ...extraProps,
        };
        logger.withMetadata(enrichedProps).debug(`[Analytics]: ${name}`);

        // AIDEV-NOTE: Determine if this is an essential event for service functionality
        // Essential events are those required for transactional emails and service operation
        const essentialEvents = [
            "signed up",
            "user login",
            "scheduled reminder",
        ];

        // Customer.io receives essential events for transactional communications (GDPR legitimate interest)
        // OR all events if user has given analytics consent
        if (getHasAnalyticsConsent() || essentialEvents.includes(name)) {
            sendCustomerIoEvent(name, enrichedProps);
        }

        // Only send to Mixpanel if user has given consent for analytics
        if (getHasAnalyticsConsent()) {
            // AIDEV-NOTE: Queue events if Mixpanel not ready, otherwise send directly
            if (!mixpanelReady) {
                eventQueue.push({ name, props: enrichedProps });
                logger
                    .withMetadata({ event: name })
                    .info("[Analytics] Event queued (Mixpanel not ready)");
            } else {
                mixpanel.track(name, enrichedProps);
            }
        }
    } catch (error) {
        if (error instanceof Error) {
            if (error.name === "TypeError") {
                logger.info("Event not sent because analytics are disabled.");
            } else {
                captureException(`Failed to track event: ${name}`, error);
            }
        } else {
            captureException(
                `Failed to track event: ${name}`,
                new Error(String(error)),
            );
        }
    }
}

export function trackEventAsUser<E extends MixpanelEvent["name"]>(
    name: E,
    sessionUserId: string,
    props: Extract<MixpanelEvent, { name: E }>["props"],
    apiClient?: { post: (url: string, data: any) => Promise<Response> },
) {
    const extraProps = getEnrichedProps();

    const enrichedProps = {
        ...props,
        ...extraProps,
    };

    logger.withMetadata(enrichedProps).debug(`[Analytics]: ${name}`);

    if (apiClient) {
        // AIDEV-NOTE: Use provided apiClient when available
        return apiClient
            .post("/analytics/track-as-user", {
                event: name,
                eventUserId: sessionUserId,
                props: enrichedProps,
            })
            .catch((error) => {
                logger.error(`[Analytics] Failed to track event: ${error}`);
                captureException("Failed to track analytics event", error);
            });
    } else {
        // AIDEV-NOTE: Fallback to fetch for backwards compatibility
        // This will be removed once all callers are updated to pass apiClient
        const API_URL = ""; // This will cause an error, encouraging migration
        return fetch(API_URL + "/analytics/track-as-user", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                event: name,
                eventUserId: sessionUserId,
                props: enrichedProps,
            }),
        }).catch((error) => {
            logger.error(`[Analytics] Failed to track event: ${error}`);
            captureException("Failed to track analytics event", error);
        });
    }
}

export const captureException = (context: string, error: unknown) => {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    logger.withError(errorObj).error(`${context}: ${errorObj.message}`);
    return Sentry.captureException(errorObj);
};

export const trackViewedModal = (
    modalName: string,
    props: Record<string, unknown> = {},
) => {
    trackEvent("viewed modal", { modal: modalName, ...props });
};

// Safari Private Browsing (and some strict mobile settings) throw a synchronous
// QuotaExceededError the first time localStorage.setItem is called. We reuse the
// same feature-detection logic we added in the Supabase client shim so that all
// analytics code degrades gracefully when persistent storage is blocked.
const isLocalStorageWritable = (() => {
    if (typeof window === "undefined") return false;
    try {
        const testKey = "__storage_test__";
        window.localStorage.setItem(testKey, testKey);
        window.localStorage.removeItem(testKey);
        return true;
    } catch {
        return false;
    }
})();

const safeGet = (key: string): string | null => {
    if (!isLocalStorageWritable) return null;
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};
const safeSet = (key: string, value: string) => {
    if (!isLocalStorageWritable) return;
    try {
        window.localStorage.setItem(key, value);
    } catch {
        /* ignore */
    }
};

// AIDEV-NOTE: Respect user consent from storage; default to false when unset
export const getHasAnalyticsConsent = () => {
    const saved = safeGet("hasConsentedToAnalytics");
    return saved === "true";
};

export const setAnalyticsConsent = (
    consent: boolean,
    user?: {
        id: string;
        email?: string;
    } | null,
    options?: {
        userType?: UserType;
    },
) => {
    try {
        // Initialize Mixpanel if not already initialized
        const mixpanelProjectToken = getBrowserEnvVar(
            "VITE_APP_MIXPANEL_PROJECT_TOKEN",
        );
        if (mixpanelProjectToken) {
            mixpanel.init(mixpanelProjectToken, {
                track_pageview: false,
                persistence: isLocalStorageWritable ? "localStorage" : "cookie",
                disable_persistence: !isLocalStorageWritable,
                ignore_dnt: true,
            });

            // AIDEV-NOTE: Mark Mixpanel as ready when consent is set
            mixpanelReady = true;
        }

        safeSet("hasConsentedToAnalytics", consent.toString());

        // AIDEV-NOTE: Emit explicit consent event for product analytics
        try {
            trackEvent("cookie consent updated", {
                status: consent ? "accepted" : "declined",
            } as any);
        } catch {
            /* ignore typing until event schema is present */
        }

        if (consent) {
            mixpanel.opt_in_tracking();
            if (user) {
                initAnalytics(user, {
                    userType: options?.userType ?? currentUserType,
                });
            }
        } else {
            mixpanel.opt_out_tracking();
        }
    } catch (error) {
        logger
            .withError(error as Error)
            .error("Failed to set analytics consent");
    }
};

export const hasUserMadeConsentChoice = () => {
    if (typeof window === "undefined") return false;
    const saved = safeGet("hasConsentedToAnalytics");
    return saved === "true" || saved === "false";
};
